import { API_BASE, LEGACY_STORAGE_KEY } from './constants.js'
import { publishAblyEvent } from '../hooks/useAblyChannel.js'

/**
 * Client for the shared Q2 Goals data store.
 *
 * Endpoints (same Cloudflare Worker the current dashboard uses):
 *   GET  /goals  — returns the full pillars array (or 204 if empty)
 *   POST /goals  — saves the full pillars array
 *
 * Fallback: if the worker is unreachable, read/write localStorage so
 * a user offline still sees something reasonable.
 */

/**
 * Fetch the shared goals data array from the worker.
 * Returns an array of pillar objects, or null if the store is empty.
 */
export async function fetchGoalsData() {
  try {
    const res = await fetch(API_BASE + '/goals')
    if (res.status === 204) return readLocalFallback()
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    if (Array.isArray(data) && data.length > 0) {
      // Keep a local cache for offline fallback
      try {
        localStorage.setItem(LEGACY_STORAGE_KEY, JSON.stringify(data))
      } catch {}
      return data
    }
    return readLocalFallback()
  } catch (err) {
    console.warn('fetchGoalsData: worker unreachable, using local cache', err)
    return readLocalFallback()
  }
}

/**
 * Save the shared goals data array.
 * Always writes to localStorage AND the worker, matching the current
 * dashboard's dual-write pattern.
 */
export async function saveGoalsData(data) {
  const json = JSON.stringify(data)
  // Local cache first (cheap, always succeeds)
  try {
    localStorage.setItem(LEGACY_STORAGE_KEY, json)
  } catch {}
  // Then POST to worker
  const res = await fetch(API_BASE + '/goals', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: json,
  })
  if (!res.ok) throw new Error(`Save failed: HTTP ${res.status}`)
  // Broadcast real-time notification so other clients refetch.
  // Fire-and-forget — if Ably is unavailable, the 15s poll catches the change.
  publishAblyEvent('goals-changed').catch(() => {})
  return data
}

/**
 * Update a single field on one goal or sub-goal via the partial-update
 * endpoint. Atomic per-field on the server — safe against concurrent
 * writers. Fires the same `goals-changed` Ably broadcast as full-state
 * saves so other clients refetch and render the update live.
 *
 * Shape:
 *   { pillarName, goalIndex, subgoalIndex?, field, value }
 */
export async function saveGoalField({ pillarName, goalIndex, subgoalIndex, field, value }) {
  const res = await fetch(API_BASE + '/goals/field', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pillarName, goalIndex, subgoalIndex, field, value }),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`Goal field save failed: HTTP ${res.status} ${msg}`)
  }
  const data = await res.json()
  publishAblyEvent('goals-changed').catch(() => {})
  return data.goals
}

/**
 * Structural goals mutation (add/remove goal, add/remove subgoal,
 * update pillar). Server applies the change server-side so concurrent
 * ops don't require the client to send the full goals array.
 *
 * Shape (one of):
 *   { op: 'addGoal',      pillarName, goal }
 *   { op: 'removeGoal',   pillarName, goalIndex }
 *   { op: 'addSubgoal',   pillarName, goalIndex, subgoal }
 *   { op: 'removeSubgoal',pillarName, goalIndex, subgoalIndex }
 *   { op: 'updatePillar', pillarName, field, value }
 */
export async function saveGoalsMutation(payload) {
  const res = await fetch(API_BASE + '/goals/mutate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload || {}),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`Goals mutation failed: HTTP ${res.status} ${msg}`)
  }
  const data = await res.json()
  publishAblyEvent('goals-changed').catch(() => {})
  return data.goals
}

/**
 * Trigger a server-side HubSpot → goals sync.
 *
 * The worker fetches Q2 closed-won deals, recomputes the Revenue sub-goal
 * `current` values (new-customer / design+advisory / TE), writes the
 * updated goals array back to KV, and returns a receipt.
 *
 * After a successful sync we broadcast `goals-changed` so every open
 * client refetches and displays the new numbers live.
 *
 * Returns the receipt: {
 *   ok, updatedAt, q2Start, q2End, dealsCountedQ2,
 *   totals: { newCustRev, designRev, teRev, totalHsRev },
 *   updatedSubgoals: [{ label, value, dynamic? }]
 * }
 */
export async function syncRevenueFromHubspot() {
  const res = await fetch(API_BASE + '/hs/sync-revenue', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`Sync failed: HTTP ${res.status} ${msg}`)
  }
  const receipt = await res.json()
  // Tell other tabs/users to refetch the goals data.
  publishAblyEvent('goals-changed').catch(() => {})
  return receipt
}

/**
 * Trigger an immediate Metabase → EO Buyouts sub-goal sync. The worker
 * pulls active Transaction Scope projects from teamwork_projects_view
 * and updates any goal with syncSource = 'metabase:eo-buyouts'. Also
 * runs every 10 min via cron.
 */
export async function syncEoBuyouts() {
  const res = await fetch(API_BASE + '/sync/eo-buyouts', { method: 'POST' })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`EO Buyouts sync failed: HTTP ${res.status} ${msg}`)
  }
  publishAblyEvent('goals-changed').catch(() => {})
  return res.json()
}

/**
 * Flip the manual close override on a synced sub-goal. Used when
 * Teamwork's completed_at lags behind reality. Setting manualClosed=false
 * clears the override.
 */
export async function setSubgoalManualClose({ pillarName, goalIndex, subgoalIndex, manualClosed, by }) {
  const res = await fetch(API_BASE + '/goals/subgoal-override', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      pillarName,
      goalIndex,
      subgoalIndex,
      manualClosed: Boolean(manualClosed),
      by: by || null,
    }),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`Sub-goal override failed: HTTP ${res.status} ${msg}`)
  }
  publishAblyEvent('goals-changed').catch(() => {})
  const data = await res.json()
  return data.goals
}

function readLocalFallback() {
  try {
    const saved = localStorage.getItem(LEGACY_STORAGE_KEY)
    if (!saved) return null
    const parsed = JSON.parse(saved)
    return Array.isArray(parsed) ? parsed : null
  } catch {
    return null
  }
}
